import * as React from 'react';
import Loader from 'components/Loader';
import { isValidSessionID } from 'utils/session';
import ErrorBoundary from '../ErrorBoundary';
import { CustomerFacingError } from 'utils/ui/errors';
import { LoaderFunctionArgs, RouteObject } from 'react-router';
import { IRoutingContext } from 'router/context';
import { loadPublicSession, userSession } from 'state/session';
import { getLocale } from 'i18n/locale';
import { IBrand, IMarket } from 'data/schema';
import { EErrorCode } from 'config/errors-codes';
import CustomerEntry from 'containers/CustomerEntry';
import { initSessionBroadcasting } from 'utils/ui/rtc';
import { tenantConfig } from 'utils/tenant';
import { clearAuthenticationStorage } from 'utils/auth';

const Stream = React.lazy(() => import('containers/Stream'));
const Legal = React.lazy(() => import('containers/LegalDocuments/Legal'));
const Imprint = React.lazy(() => import('containers/LegalDocuments/Imprint'));
const EndSession = React.lazy(() => import('containers/EndSession'));

async function contextLoader({ params }: LoaderFunctionArgs): Promise<IRoutingContext> {
  const { brand, market, sessionId } = params;
  userSession().setValues({
    brand: brand as IBrand,
    market: market as IMarket,
    locale: getLocale(market as IMarket),
  });
  if (sessionId && isValidSessionID(sessionId) && userSession().get('id') !== sessionId) {
    await loadPublicSession(sessionId);
  }
  return { userType: 'customer' };
}

async function customerSessionLoader(loaderOptions: LoaderFunctionArgs) {
  const { sessionId: sessionIdFromURL } = loaderOptions.params;
  if (!sessionIdFromURL || !isValidSessionID(sessionIdFromURL)) {
    console.log('Invalid session ID:', sessionIdFromURL, typeof sessionIdFromURL);
    throw new CustomerFacingError(EErrorCode.INVALID_SESSION_ID);
  }

  // Clear authentication state to ensure customer view loads without dealer permissions
  // This is critical when opening customer view from an authenticated dealer session,
  // especially when opened via Web Component from a different domain where sessionStorage.clear()
  // cannot access the target domain's storage due to cross-origin restrictions
  try {
    clearAuthenticationStorage();
  } catch (error) {
    // Log but don't fail - customer view should still load even if clearing storage fails
    console.warn('[customerSessionLoader] Failed to clear authentication storage:', error);
  }

  try {
    // Ensure session is loaded before initializing broadcasting to avoid race conditions
    // Only load if not already loaded by parent contextLoader
    if (userSession().get('id') !== sessionIdFromURL) {
      await loadPublicSession(sessionIdFromURL);
    }

    // Initialize broadcasting AFTER session is loaded to ensure session ID is available
    initSessionBroadcasting();
  } catch (error) {
    // AVS-4080: Preserve SESSION_NOT_FOUND (and any other specific code) from the
    // underlying ApiError/CustomerFacingError so the error boundary shows the right
    // copy ("session was cancelled or rescheduled") instead of the generic
    // "Cannot load session" message.
    const code = (error as { code?: EErrorCode })?.code ?? EErrorCode.CANT_LOAD_SESSION;
    throw new CustomerFacingError(code);
  }

  // AVS-4080: The scheduler's "delete" is a soft-delete (sets endedAt, removes
  // scheduledStartTime). A customer opening that link gets a 200 with an ended,
  // never-shared session. Surface this as SESSION_NOT_FOUND so the user sees the
  // cancelled-or-rescheduled message instead of the waiting room.
  const session = userSession().get();
  if (session?.endedAt && !session.isShared) {
    throw new CustomerFacingError(EErrorCode.SESSION_NOT_FOUND);
  }

  // do not proceed until config is loaded
  await tenantConfig().configLoaded;

  return { userType: 'customer' };
}

const localizedRoutes: RouteObject[] = [
  {
    path: '/:brand/:market/view/:sessionId/goodbye',
    element: (
      <React.Suspense fallback={<Loader />}>
        <EndSession />
      </React.Suspense>
    ),
  },
  {
    path: '/:brand/:market/view',
    element: <CustomerEntry />,
    loader: customerSessionLoader,
    children: [
      {
        path: ':sessionId',
        element: (
          <React.Suspense fallback={<Loader />}>
            <Stream expanded />
          </React.Suspense>
        ),
      },
    ],
  },
  {
    path: '/:brand/:market/legal',
    element: (
      <React.Suspense fallback={<Loader />}>
        <Legal />
      </React.Suspense>
    ),
  },
  {
    path: '/:brand/:market/imprint',
    element: (
      <React.Suspense fallback={<Loader />}>
        <Imprint />
      </React.Suspense>
    ),
  },

  // add error boundary to all customer routes
].map((route) => ({ ...route, errorElement: <ErrorBoundary /> }));

export const customerRoutes: RouteObject[] = [
  {
    path: '/:brand/:market',
    children: localizedRoutes,
    loader: contextLoader,
  },
];
